import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  X,
  Car,
  Clock,
  AlertTriangle,
  Users,
  Hash,
  Palette,
  Share2,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const POLL_MS = 5000;

/** Bottom sheet shown when the current user is either:
 *   - The LEAVER of a spot that has just been claimed, or
 *   - The CLAIMER of a spot they are heading to.
 *  Shows the other driver's identity + wait_mode + confirmation buttons.
 */
export default function HandoverPanel({ spot, onSettled }) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [state, setState] = React.useState({
    handover_state: spot?.handover_state || "pending",
    wait_mode: spot?.wait_mode || "waiting",
    leaver_profile: spot?.leaver_profile || null,
    claimer_profile: spot?.claimer_profile || null,
    role: spot?.mine ? "leaver" : "claimer",
  });

  // Poll the handover endpoint so both drivers see the other side's confirmations.
  React.useEffect(() => {
    if (!spot?.id) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/spots/${spot.id}/handover`);
        if (!alive) return;
        setState((s) => ({ ...s, ...data }));
        if (data.handover_state && data.handover_state !== "pending") {
          // Terminal-ish; keep polling briefly then let parent clean up.
        }
      } catch {
        // silent — the spot may have been removed or the claim expired.
      }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [spot?.id]);

  const isLeaver = state.role === "leaver";
  const other = isLeaver ? state.claimer_profile : state.leaver_profile;
  const waitMode = state.wait_mode;
  const handoverState = state.handover_state || "pending";

  const claimExpiresAt = spot?.claim_expires_at ? new Date(spot.claim_expires_at).getTime() : null;
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secsLeft = claimExpiresAt ? Math.max(0, Math.round((claimExpiresAt - nowMs) / 1000)) : 0;
  const mm = Math.floor(secsLeft / 60);
  const ss = String(secsLeft % 60).padStart(2, "0");

  const post = async (event) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/spots/${spot.id}/handover`, { event });
      setState((s) => ({ ...s, handover_state: data.handover_state }));
      toast.success(_toastFor(event));
      // Give the user a beat to read state, then let the parent refresh.
      setTimeout(() => onSettled && onSettled(data), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const me = _me();
    if (!me) return;
    const text = `${secsLeft > 0 ? `${mm}:${ss} away` : "On my way"} · ${me.car_color || ""} ${me.car_model || ""} · ${me.plate_last3 || ""}`.trim();
    try {
      if (navigator.share) {
        await navigator.share({ text, title: "SpotDrop — my ETA" });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("ETA copied to clipboard.");
      }
    } catch {
      /* user cancelled or unsupported */
    }
  };

  const banner = _stateBanner(handoverState, isLeaver);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="fixed inset-x-3 bottom-3 z-[1900] rounded-3xl bg-[#141416] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.55)] sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:max-w-[440px] sm:w-[94vw]"
        data-testid="handover-panel"
      >
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between px-4 py-3"
          data-testid="handover-toggle"
        >
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isLeaver ? "bg-[#ccff00]/15 border border-[#ccff00]/30" : "bg-amber-500/15 border border-amber-500/30"
            }`}>
              <Users className={`w-4 h-4 ${isLeaver ? "text-[#ccff00]" : "text-amber-300"}`} />
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Handover</div>
              <div className="text-sm text-white font-semibold leading-tight" data-testid="handover-role">
                {isLeaver ? "Driver heading to your spot" : "Waiting for you to arrive"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {secsLeft > 0 && (
              <div className="inline-flex items-center gap-1 px-2 h-7 rounded-full bg-[#0a0a0c] border border-white/10 text-[11px] text-zinc-200 font-mono" data-testid="handover-countdown">
                <Clock className="w-3 h-3" /> {mm}:{ss}
              </div>
            )}
            <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${collapsed ? "" : "rotate-180"}`} />
          </div>
        </button>

        {!collapsed && (
          <div className="px-4 pb-5">
            {banner && (
              <div className={`mb-3 p-2.5 rounded-lg text-[12px] border flex items-start gap-2 ${banner.tone}`} data-testid="handover-state-banner">
                {banner.icon}
                <span>{banner.text}</span>
              </div>
            )}

            {other ? (
              <DriverCard profile={other} isLeaver={isLeaver} waitMode={waitMode} />
            ) : (
              <div className="p-3 rounded-xl bg-[#0a0a0c] border border-white/5 text-[12px] text-zinc-400 flex items-center gap-2" data-testid="handover-waiting">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {isLeaver ? "Waiting for a driver to head over…" : "Waiting for the leaving driver…"}
              </div>
            )}

            {handoverState === "pending" && other && (
              <div className="mt-3 grid grid-cols-1 gap-2">
                {isLeaver ? (
                  <>
                    <ActionBtn primary onClick={() => post("leaver_confirmed")} disabled={busy} testid="handover-leaver-confirm">
                      <Check className="w-4 h-4" /> Yes, correct driver
                    </ActionBtn>
                    <div className="grid grid-cols-2 gap-2">
                      <ActionBtn onClick={() => post("leaver_rejected")} disabled={busy} testid="handover-leaver-reject" tone="amber">
                        <X className="w-4 h-4" /> No, wrong car
                      </ActionBtn>
                      <ActionBtn onClick={() => post("leaver_taken")} disabled={busy} testid="handover-leaver-taken" tone="red">
                        Someone else took the spot
                      </ActionBtn>
                    </div>
                    <ActionBtn onClick={() => post("leaver_abandoned")} disabled={busy} testid="handover-leaver-abandon" tone="ghost">
                      <Clock className="w-4 h-4" /> I can&apos;t wait anymore
                    </ActionBtn>
                  </>
                ) : (
                  <>
                    <ActionBtn primary onClick={() => post("claimer_received")} disabled={busy} testid="handover-claimer-received">
                      <Check className="w-4 h-4" /> Spot received
                    </ActionBtn>
                    <ActionBtn onClick={() => post("claimer_arrived")} disabled={busy} testid="handover-claimer-arrived" tone="ghost">
                      I arrived
                    </ActionBtn>
                    <div className="grid grid-cols-2 gap-2">
                      <ActionBtn onClick={() => post("claimer_taken")} disabled={busy} testid="handover-claimer-taken" tone="red">
                        Spot was taken
                      </ActionBtn>
                      <ActionBtn onClick={() => post("claimer_no_leaver")} disabled={busy} testid="handover-claimer-no-leaver" tone="amber">
                        Leaver not there
                      </ActionBtn>
                    </div>
                    <button
                      type="button"
                      onClick={share}
                      className="mt-1 h-10 rounded-full bg-transparent border border-white/10 text-zinc-300 text-[12px] font-medium inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
                      data-testid="handover-share-eta"
                    >
                      <Share2 className="w-3.5 h-3.5" /> Share ETA
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function DriverCard({ profile, isLeaver, waitMode }) {
  return (
    <div className="p-3 rounded-xl bg-[#0a0a0c] border border-white/5" data-testid="handover-other-card">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
          <Car className="w-4 h-4 text-[#ccff00]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white leading-tight" data-testid="handover-other-nickname">
            {profile.nickname || "Driver"}
          </div>
          <div className="text-[11px] text-zinc-400 leading-tight mt-0.5 flex items-center gap-1.5">
            <Palette className="w-3 h-3" />
            <span data-testid="handover-other-car">
              {[profile.car_color, profile.car_model].filter(Boolean).join(" ") || "—"}
            </span>
            {profile.plate_last3 && (
              <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-[#ccff00]/15 border border-[#ccff00]/25 text-[10px] font-mono text-[#ccff00]" data-testid="handover-other-plate">
                <Hash className="w-2.5 h-2.5" />{profile.plate_last3}
              </span>
            )}
          </div>
        </div>
      </div>
      {!isLeaver && (
        <div className={`mt-2 p-2 rounded-lg text-[11px] flex items-start gap-1.5 ${
          waitMode === "waiting"
            ? "bg-[#ccff00]/10 border border-[#ccff00]/25 text-[#ccff00]"
            : "bg-amber-500/10 border border-amber-500/30 text-amber-200"
        }`} data-testid="handover-wait-banner">
          {waitMode === "waiting" ? (
            <><Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Leaving driver will wait a few minutes. Hurry safely.</>
          ) : (
            <><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Leaving driver isn&apos;t waiting — spot may go quickly.</>
          )}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, testid, primary, tone }) {
  const cls = primary
    ? "bg-[#ccff00] text-[#0a0a0c] font-semibold"
    : tone === "red"
      ? "bg-red-500/15 border border-red-500/30 text-red-200"
      : tone === "amber"
        ? "bg-amber-500/15 border border-amber-500/30 text-amber-100"
        : tone === "ghost"
          ? "bg-transparent border border-white/10 text-zinc-200"
          : "bg-[#141416] border border-white/10 text-white";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`h-11 rounded-xl text-[12px] leading-tight inline-flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98] transition-transform px-2 ${cls}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function _me() {
  try {
    return JSON.parse(localStorage.getItem("spotdrop_driver_profile"));
  } catch { return null; }
}

function _toastFor(event) {
  switch (event) {
    case "leaver_confirmed": return "Nice handover — see you next time.";
    case "claimer_received": return "Nice handover — see you next time.";
    case "leaver_rejected": return "Marked as wrong car. Claim released.";
    case "leaver_abandoned": return "Marked as leaving now.";
    case "leaver_taken": return "Marked as taken by someone else.";
    case "claimer_arrived": return "Marked as arrived.";
    case "claimer_taken": return "Marked as spot taken.";
    case "claimer_no_leaver": return "Marked as leaver-not-there.";
    default: return "Handover updated.";
  }
}

function _stateBanner(state, isLeaver) {
  if (state === "pending") return null;
  const map = {
    confirmed: { text: "Handover confirmed by both drivers.", tone: "bg-[#ccff00]/10 border-[#ccff00]/25 text-[#ccff00]", icon: <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> },
    arrived: { text: isLeaver ? "The other driver arrived." : "You marked as arrived.", tone: "bg-[#ccff00]/10 border-[#ccff00]/25 text-[#ccff00]", icon: <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> },
    rejected: { text: "You reported the wrong car. Claim was released.", tone: "bg-amber-500/10 border-amber-500/30 text-amber-100", icon: <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> },
    abandoned_by_leaver: { text: "Leaving driver couldn't wait any longer.", tone: "bg-amber-500/10 border-amber-500/30 text-amber-100", icon: <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> },
    taken_by_someone_else: { text: "Spot was taken by someone else.", tone: "bg-red-500/10 border-red-500/30 text-red-200", icon: <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> },
    leaver_no_show: { text: "Leaving driver wasn't at the spot.", tone: "bg-red-500/10 border-red-500/30 text-red-200", icon: <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> },
  };
  return map[state] || null;
}
